import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  MenuList,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ButtonGroup,
  Popper,
  Grow,
  ClickAwayListener
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { makeApiRequest } from '../../../utils/api';
import excelLogo from '../../../assets/5b60cdcc-dbe2-4718-8be4-b33c4c2c3efa.jpg';

// Date formatting functions
const formatDatewithMonth = (date) => {
  if (!date || isNaN(Date.parse(date))) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};

const convertToAPIDate = (dateString) => {
  if (!dateString) return '';
  const [month, day, year] = dateString.split('-');
  return `${year}-${month}-${day}`;
};

const convertToDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${month}-${day}-${year}`;
};

// Parse Excel date to Y-m-d
const formatExcelDate = (date) => {
  if (!date) return '';
  let parsedDate;
  if (typeof date === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const jsDate = new Date(excelEpoch.getTime() + date * 24 * 60 * 60 * 1000);
    if (isNaN(jsDate.getTime())) {
      return '';
    }
    parsedDate = dayjs(jsDate);
  } else {
    parsedDate = dayjs(String(date), ['D/M/YY', 'DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD']);
    if (!parsedDate.isValid()) {
      return '';
    }
  }
  const result = parsedDate.format('YYYY-MM-DD');
  return result;
};

const AddNewLead = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    email: '',
    passoutYear: '',
    hometown: '',
    followUpDate: '',
    department: '',
    course: '',
    channel: '',
    status: '',
    remarks: '',
    college: ''
  });
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [errorData, setErrorData] = React.useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const [duplicateData, setDuplicateData] = React.useState([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const excelOptions = ['Download Template', 'Upload Excel File'];

  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [dateError, setDateError] = useState('');
  const [passoutYearError, setPassoutYearError] = useState('');

  const channelOptions = [
    'Website',
    'Phone',
    'Email',
    'Social Media',
    'Data follow',
    'Webinar',
    'Placement Drive',
    'Corporates',
    'Direct',
    'Companies',
    'Reference'
  ];

  const disableWeekends = (date) => {
    const day = date.day();
    return day === 0 || day === 6;
  };

  const calculateDefaultFollowUpDate = () => {
    const today = dayjs();
    let defaultDate = today.add(7, 'day');
    const day = defaultDate.day();
    if (day === 6) {
      defaultDate = defaultDate.subtract(1, 'day');
    } else if (day === 0) {
      defaultDate = defaultDate.add(1, 'day');
    }
    return defaultDate.format('YYYY-MM-DD');
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      followUpDate: calculateDefaultFollowUpDate()
    }));
  }, []);

  React.useEffect(() => {
    const fetchCourses = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await makeApiRequest('post', '/api/course/list', { token });
        if (response && response.status === 'success') {
          setCourses(response.data);
        }
      } catch (error) {
        console.error('API Error:', error);
      }
    };
    fetchCourses();
  }, []);

  React.useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await makeApiRequest('post', '/api/departments', { token });
        if (response && response.status === 'success') {
          setDepartments(response.data);
        }
      } catch (error) {
        console.error('API Error:', error);
      }
    };
    fetchDepartments();
  }, []);

  const validateEmail = (email) => {
    if (!email) return { isValid: true, error: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, error: 'Invalid email format' };
    }
    return { isValid: true, error: '' };
  };

  const validateName = (name) => {
    if (!name.trim()) {
      return { isValid: false, error: 'Name is required' };
    }
    return { isValid: true, error: '' };
  };

  const validatePhone = (phone) => {
    if (!phone) {
      return { isValid: false, error: 'Phone number is required' };
    } else if (phone.length !== 10) {
      return { isValid: false, error: 'Phone number must be 10 digits' };
    }
    return { isValid: true, error: '' };
  };

  const validatePassoutYear = (passoutYear) => {
    if (passoutYear && passoutYear.length > 4) {
      return { isValid: false, error: 'Pass-out year must not exceed 4 digits' };
    }
    return { isValid: true, error: '' };
  };

 const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === 'phone') {
    const numericValue = value.replace(/[^0-9]/g, '');
    setPhoneError('');
    setIsPhoneValid(true);
    setFormData({ ...formData, [name]: numericValue });
  } else if (name === 'name') {
    setNameError('');
    setFormData({ ...formData, [name]: value });
  } else if (name === 'email') {
    setEmailError('');
    setFormData({ ...formData, [name]: value });
  } else {
    setFormData({ ...formData, [name]: value });
  }
};


  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      const { isValid, error } = validateName(value);
      setNameError(error);
    } else if (name === 'phone') {
      const { isValid, error } = validatePhone(value);
      setPhoneError(error);
      setIsPhoneValid(isValid);
    } else if (name === 'email') {
      const { isValid, error } = validateEmail(value);
      setEmailError('');
      setFormData({ ...formData, [name]: value });
    } else if (name === 'passoutYear') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setPassoutYearError('');
      setFormData({ ...formData, [name]: numericValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleNameBlur = () => {
    if (!formData.name.trim()) {
      setNameError('Name is required');
    } else {
      setNameError('');
    }
  };

  const handlePhoneBlur = () => {
    const phone = formData.phone;
    if (!phone) {
      setPhoneError('Phone number is required');
      setIsPhoneValid(false);
    } else if (phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      setIsPhoneValid(false);
    } else {
      setPhoneError('');
      setIsPhoneValid(true);
    }
  };

  const handleEmailBlur = () => {
    const email = formData.email;
    if (email) {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!emailRegex.test(email)) {
        setEmailError('Invalid email format');
      } else if (/[A-Z]/.test(email)) {
        setEmailError('Uppercase letters not allowed. Use lowercase only.');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  };

  const handlePassoutYearBlur = () => {
    const passoutYear = formData.passoutYear;
    if (passoutYear && passoutYear.length > 4) {
      setPassoutYearError('Pass-out year must not exceed 4 digits');
    } else {
      setPassoutYearError('');
    }
  };

  const handleDateChange = (newValue) => {
    if (!newValue) {
      setDateError('');
      setFormData({ ...formData, followUpDate: '' });
      return;
    }
    const date = dayjs(newValue);
    if (!date.isValid()) {
      setDateError('Invalid date');
      setFormData({ ...formData, followUpDate: '' });
    } else {
      const dayOfWeek = date.day();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        setDateError('Follow-up date cannot be on Saturday or Sunday');
        setFormData({ ...formData, followUpDate: '' });
      } else {
        setDateError('');
        setFormData({ ...formData, followUpDate: date.format('YYYY-MM-DD') });
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await makeApiRequest('post', '/api/download-template', { token });
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Error downloading template');
    }
    setOpen(false);
  };
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload an .xlsx file', { autoClose: 10000 });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 1) {
            toast.error('Excel file is empty', { autoClose: 10000 });
            return;
          }

          const headers = jsonData[0].map(h => h.trim());
          const requiredHeaders = ['name', 'phone'];
          const expectedHeaders = [
            'name', 'phone', 'email', 'passoutYear', 'hometown', 'followUpDate',
            'department', 'course', 'channel', 'status', 'remarks', 'college'
          ];

          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          if (missingHeaders.length > 0) {
            toast.error(`Missing required headers: ${missingHeaders.join(', ')}`, { autoClose: 10000 });
            return;
          }

          const headerToIndex = headers.reduce((map, header, index) => {
            map[header] = index;
            return map;
          }, {});

          const leads = jsonData.slice(1).map((row, rowIndex) => {
            const lead = { rowNum: rowIndex + 2 };
            headers.forEach((header, index) => {
              if (expectedHeaders.includes(header)) {
                let key = header.toLowerCase();
                if (key === 'passoutyear') key = 'pass_out_year';
                if (key === 'followupdate') key = 'next_followup_date';
                if (key === 'department') key = 'department_name';
                if (key === 'course') key = 'course_name';
                let value = row[index] != null ? String(row[index]).trim() : null;
                if (key === 'next_followup_date' && value) {
                  value = formatExcelDate(row[index]);
                }
                if (key === 'pass_out_year' && value) {
                  value = parseInt(value, 10) || null;
                }
                lead[key] = value;
              }
            });
            return lead;
          }).filter(lead => lead.name && lead.phone);

          if (leads.length === 0) {
            toast.error('No valid data found in the Excel file', { autoClose: 10000 });
            return;
          }

          const phoneMap = new Map();
          const emailMap = new Map();
          const duplicates = [];

          for (let i = 0; i < leads.length; i++) {
            const lead = leads[i];
            const rowNum = lead.rowNum;

            if (lead.phone && !/^[0-9]{10}$/.test(lead.phone)) {
              duplicates.push({
                name: lead.name,
                error: [{
                  cell: `B${rowNum}`,
                  content: lead.phone,
                  error: `Phone number must be 10 digits in Row ${rowNum}`
                }]
              });
            } else if (lead.phone) {
              if (phoneMap.has(lead.phone)) {
                const prevRow = phoneMap.get(lead.phone);
                const cell = `B${rowNum}`;
                duplicates.push({
                  name: lead.name,
                  error: [{
                    cell,
                    content: lead.phone,
                    error: `Duplicate phone number in Row ${rowNum} (also in Row ${prevRow})`
                  }]
                });
              } else {
                phoneMap.set(lead.phone, rowNum);
              }
            }

            if (lead.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) {
              duplicates.push({
                name: lead.name,
                error: [{
                  cell: `C${rowNum}`,
                  content: lead.email,
                  error: `Invalid email format in Row ${rowNum}`
                }]
              });
            } else if (lead.email && lead.email.trim()) {
              if (emailMap.has(lead.email)) {
                const prevRow = emailMap.get(lead.email);
                const cell = `C${rowNum}`;
                duplicates.push({
                  name: lead.name,
                  error: [{
                    cell,
                    content: lead.email,
                    error: `Duplicate email in Row ${rowNum} (also in Row ${prevRow})`
                  }]
                });
              } else {
                emailMap.set(lead.email, rowNum);
              }
            }

            if (lead.pass_out_year && (isNaN(lead.pass_out_year) || String(lead.pass_out_year).length !== 4)) {
              duplicates.push({
                name: lead.name,
                error: [{
                  cell: `D${rowNum}`,
                  content: lead.pass_out_year,
                  error: `Pass-out year must be a 4-digit number in Row ${rowNum}`
                }]
              });
            }

            const validStatuses = ['New', 'In-progress', 'Deferred', 'Lost', 'Converted', 'Overdue'];
            if (lead.status && !validStatuses.includes(lead.status)) {
              duplicates.push({
                name: lead.name,
                error: [{
                  cell: `J${rowNum}`,
                  content: lead.status,
                  error: `Invalid status in Row ${rowNum}. Must be one of: ${validStatuses.join(', ')}`
                }]
              });
            }

            const validChannels = ['Website', 'Phone', 'Email', 'Referral', 'Social Media'];
            if (lead.channel && !validChannels.includes(lead.channel)) {
              duplicates.push({
                name: lead.name,
                error: [{
                  cell: `I${rowNum}`,
                  content: lead.channel,
                  error: `Invalid channel in Row ${rowNum}. Must be one of: ${validChannels.join(', ')}`
                }]
              });
            }
          }

          if (duplicates.length > 0) {
            setDuplicateData(duplicates);
            setDuplicateDialogOpen(true);
            return;
          }

          const cleanLeads = leads.map(lead => {
            const { rowNum, ...rest } = lead;
            return rest;
          });

          const token = localStorage.getItem('authToken');
          const response = await makeApiRequest('post', '/api/import-leads', {
            token,
            leads: cleanLeads
          });

          if (response.status === 'success' && response.data.errors.length === 0) {
            toast.success(`${response.data.imported} leads imported successfully`, { autoClose: 10000 });
          } else {
            const errorArray = response.message?.message || response.data.errors || [];
            setErrorData(errorArray);
            setErrorDialogOpen(true);
            toast.error('Some leads failed to import due to errors', { autoClose: 10000 });
          }
        } catch (error) {
          console.error('API Error Details:', error.response?.data, error.message);
          const errorArray = error.response?.data?.message?.message || [];
          if (errorArray.length > 0) {
            setErrorData(errorArray);
            setErrorDialogOpen(true);
            toast.error('Some leads failed to import due to errors', { autoClose: 10000 });
          } else {
            toast.error(error.response?.data?.message || 'Error importing leads', { autoClose: 10000 });
          }
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('File Reading Error:', error.message);
      toast.error('Error reading Excel file', { autoClose: 10000 });
    }
    setOpen(false);
    fileInputRef.current.value = '';
  };


  const handleExcelClick = () => {
    if (selectedIndex === 0) {
      handleDownloadTemplate();
    } else if (selectedIndex === 1) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      } else {
        toast.error('File input not found');
      }
    }
  };

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
    if (index === 0) {
      handleDownloadTemplate();
    } else if (index === 1) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      } else {
        toast.error('File input not found');
      }
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNameError('');
    setPhoneError('');
    setEmailError('');
    setDateError('');
    setPassoutYearError('');
    const submissionData = {
      ...formData,
      email: formData.email ? formData.email.toLowerCase() : '',
    };

    let isValid = true;
    if (!submissionData.name.trim()) {
      setNameError('Name is required');
      isValid = false;
    }

    if (!submissionData.phone) {
      setPhoneError('Phone number is required');
      setIsPhoneValid(false);
      isValid = false;
    } else if (submissionData.phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      setIsPhoneValid(false);
      isValid = false;
    }

    //     if (formData.email) {
    //       const emailValidation = validateEmail(formData.email);
    //       if (!emailValidation.isValid) {
    //         setEmailError(emailValidation.error);
    if (submissionData.email) {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!emailRegex.test(submissionData.email)) {
        setEmailError('Invalid email format (lowercase only)');
        isValid = false;
      }
    }

    if (submissionData.followUpDate) {
      const date = dayjs(submissionData.followUpDate);
      if (!date.isValid()) {
        setDateError('Invalid date');
        isValid = false;
      } else {
        const dayOfWeek = date.day();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          setDateError('Follow-up date cannot be on Saturday or Sunday');
          isValid = false;
        }
      }
    }

    //     const passoutYearValidation = validatePassoutYear(formData.passoutYear);
    //     if (!passoutYearValidation.isValid) {
    //       setPassoutYearError(passoutYearValidation.error);
    if (submissionData.passoutYear && submissionData.passoutYear.length > 4) {
      setPassoutYearError('Pass-out year must not exceed 4 digits');
      isValid = false;
    }

    if (!isValid) {
      toast.error('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const apiData = {
        token: localStorage.getItem('authToken'),
        name: submissionData.name,
        phone: submissionData.phone,
        email: submissionData.email || null,
        pass_out_year: submissionData.passoutYear || null,
        hometown: submissionData.hometown || null,
        next_followup_date: submissionData.followUpDate || null,
        department_name: submissionData.department || null,
        course_name: submissionData.course || null,
        channel: submissionData.channel || null,
        status: submissionData.status || 'New',
        remarks: submissionData.remarks || null,
        college: submissionData.college || null,
        created_by: localStorage.getItem('username'),
        created_date: new Date().toISOString().split('T')[0],
      };

      const response = await makeApiRequest('post', '/api/addLead', apiData);

      if (response && response.status === 'success') {
        toast.success('Lead created successfully');
        setFormData({
          name: '',
          phone: '',
          email: '',
          passoutYear: '',
          hometown: '',
          followUpDate: calculateDefaultFollowUpDate(),
          department: '',
          course: '',
          channel: '',
          status: '',
          remarks: '',
          college: '',
        });
        setDateError('');
        setPassoutYearError('');
      } else {
        toast.error(response?.message || 'Failed to create lead');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      passoutYear: '',
      hometown: '',
      followUpDate: calculateDefaultFollowUpDate(),
      department: '',
      course: '',
      channel: '',
      status: '',
      remarks: '',
      college: ''
    });
    setNameError('');
    setPhoneError('');
    setEmailError('');
    setDateError('');
    setPassoutYearError('');
    setIsPhoneValid(true);
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorData([]);
  };

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateData([]);
  };

  return (
    <div style={{ maxWidth: '100%', padding: '8px 30px', backgroundColor: "#ECEDFF", fontFamily: "Roboto", minHeight: "100vh" }}>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
      <Box sx={{ marginTop: "5px", height: "42px", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "#ECEDFF" }}>
        <Typography variant="h4" sx={{ color: "#481E82", fontWeight: "500", marginTop: '30px' }}>Add New Lead</Typography>
        <Box sx={{ width: "242px" }}>
          <ButtonGroup
            variant="contained"
            ref={anchorRef}
            aria-label="Excel import button group"
            disableElevation
            sx={{
              width: "242px",
              height: "60px",
              borderRadius: "10px",
              marginTop: "30px",
              background: "linear-gradient(275.76deg, #6AB04C 44.33%, #BADC58 98.56%)",
              color: 'white',
              textTransform: 'none',
              fontSize: '20px',
              fontWeight: 500,
              boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                background: "linear-gradient(275.76deg, #5a9f3b 44.33%, #a9cb47 98.56%)",
              },
              '& .MuiButtonGroup-grouped': {
                borderRight: 'none !important',
                background: "transparent",
                '&:hover': {
                  background: "transparent",
                }
              }
            }}
          >
            <Button
              variant="contained"
              startIcon={
                <Box sx={{
                  width: "35px",
                  height: "35px",
                  borderRadius: "100%",
                  backgroundColor: "#BADC58",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid white",
                  marginRight: "10px"
                }}>
                  <img
                    src={excelLogo}
                    alt="Excel logo"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                  />
                </Box>
              }
              sx={{
                fontSize: "18px",
                background: "linear-gradient(275.76deg, #6AB04C 44.33%, #BADC58 98.56%)",
                height: "60px",
                width: "242px",
                borderRadius: '6px',
                '&:hover': { backgroundColor: "#8BC34A" },
                textTransform: 'none',
                justifyContent: 'flex-start',
                paddingLeft: '20px',
                gap: '6px'
              }}
            >
              Excel Import
            </Button>
            <Button
              size="small"
              aria-controls={open ? 'excel-split-button-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label="select excel action"
              aria-haspopup="menu"
              onClick={handleToggle}
              sx={{
                color: 'white',
                minWidth: '40px',
                padding: '0 8px'
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{ zIndex: 1 }}
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper sx={{ width: '242px', boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' }}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="excel-split-button-menu" autoFocusItem>
                      {excelOptions.map((option, index) => (
                        <MenuItem
                          key={option}
                          selected={index === selectedIndex}
                          onClick={(event) => handleMenuItemClick(event, index)}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <ListItemIcon>
                            {index === 0 ? <DownloadIcon fontSize="small" /> : <UploadFileIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText>{option}</ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </Box>
      </Box>
      <Dialog open={errorDialogOpen} onClose={handleCloseErrorDialog} maxWidth="md" fullWidth>
        <DialogTitle>Errors in Import</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cell</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorData.map((entry, index) =>
                  entry.error.map((err, errIndex) => (
                    <TableRow key={`${index}-${errIndex}`}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>{err.cell}</TableCell>
                      <TableCell>{err.error}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={handleCloseErrorDialog}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={duplicateDialogOpen} onClose={handleCloseDuplicateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Duplicate Entries in Excel File</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cell</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicateData.map((entry, index) =>
                  entry.error.map((err, errIndex) => (
                    <TableRow key={`${index}-${errIndex}`}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>{err.cell}</TableCell>
                      <TableCell>{err.error}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={handleCloseDuplicateDialog}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Card sx={{ marginTop: "40px", marginBottom: "40px", padding: "20px 30px" }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: '20px' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 10 },
                }}
              >
                <TextField
                  required
                  label="Name"
                  name="name"
                  placeholder="Enter name"
                  fullWidth
                  size="small"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleNameBlur}
                  InputLabelProps={{ shrink: true }}
                  error={!!nameError}
                  helperText={nameError}
                  sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                />
                <TextField
                  required
                  label="Phone Number"
                  name="phone"
                  placeholder="Enter Phone Number"
                  fullWidth
                  size="small"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handlePhoneBlur}
                  InputLabelProps={{ shrink: true }}
                  error={!!phoneError}
                  helperText={phoneError}
                  sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 10 },
                }}
              >
                <TextField
                  label="Email"
                  name="email"
                  placeholder="Enter Email"
                  fullWidth
                  size="small"
                  value={formData.email}
                  onChange={handleChange}
                  //                   onBlur={handleBlur}
                  onBlur={handleEmailBlur}
                  InputLabelProps={{ shrink: true }}
                  error={!!emailError}
                  helperText={emailError}
                />
                <TextField
                  label="Pass-out Year"
                  name="passoutYear"
                  placeholder="Enter Pass-out Year"
                  fullWidth
                  size="small"
                  value={formData.passoutYear}
                  onChange={handleChange}
                  onBlur={handlePassoutYearBlur}
                  InputLabelProps={{ shrink: true }}
                  error={!!passoutYearError}
                  helperText={passoutYearError}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 10 },
                }}
              >
                <TextField
                  label="Hometown"
                  name="hometown"
                  placeholder="Enter Hometown"
                  fullWidth
                  size="small"
                  value={formData.hometown}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="College Name"
                  name="college"
                  placeholder="Enter College Name"
                  fullWidth
                  size="small"
                  value={formData.college}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 10 },
                  width: '100%',
                }}
              >
                <Autocomplete
                  value={formData.department}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      department: newValue || '',
                    });
                  }}
                  options={departments.length === 0 ? ['Loading...'] : departments.map((dept) => dept.department_name)}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      placeholder="Select Department"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  sx={{
                    flex: { md: 1 },
                    width: '100%',
                  }}
                />
                <Autocomplete
                  value={courses.find((c) => c.course_name === formData.course) || null}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      course: newValue ? newValue.course_name : '',
                    });
                  }}
                  options={courses}
                  getOptionLabel={(option) => option.course_name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Course"
                      placeholder="Select Course"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  sx={{
                    flex: { md: 1 },
                    width: '100%',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 10 },
                }}
              >
                <Autocomplete
                  value={formData.channel}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      channel: newValue || '',
                    });
                  }}
                  options={channelOptions}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Channel"
                      placeholder="Select Channel"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  sx={{ flex: { md: 1 } }}
                />
                <TextField
                  label="Status"
                  name="status"
                  select
                  fullWidth
                  size="small"
                  value={formData.status}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: { md: 1 } }}
                >
                  <MenuItem value="In-progress">In-progress</MenuItem>
                  <MenuItem value="Deferred">Deferred</MenuItem>
                  <MenuItem value="Lost">Lost</MenuItem>
                  <MenuItem value="Converted">Converted</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </TextField>
              </Box>
              <TextField
                label="Remarks"
                name="remarks"
                placeholder="Enter Remarks"
                fullWidth
                multiline
                rows={4}
                value={formData.remarks}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: '100px',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    boxSizing: 'border-box',
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'center',
                  gap: 3,
                  mt: 2,
                  p: 3,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#f9f9ff',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    minWidth: '200px',
                    color: '#481E82',
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  Next Follow-Up Date
                </Typography>
                <DatePicker
                  value={formData.followUpDate ? dayjs(formData.followUpDate) : null}
                  onChange={handleDateChange}
                  format="DD MMM YYYY"
                  shouldDisableDate={disableWeekends}
                  slotProps={{
                    textField: {
                      size: 'large',
                      error: !!dateError,
                      helperText: dateError,
                      sx: {
                        width: { xs: '100%', md: '350px' },
                        '& .MuiInputBase-input': {
                          fontSize: '1.2rem',
                          padding: '14px',
                          color: '#481E82',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1.2rem'
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'black',
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'black',
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: 'rgb(249, 90, 90)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgb(220, 70, 70)' }
                  }}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  CANCEL
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'SUBMITTING...' : 'SUBMIT'}
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddNewLead;
